import {
  computed,
  inject,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from "@angular/core";
import {
  CreateUserWeeklyScheduleExceptionDto,
  ResponseUserWeeklyScheduleDto,
  ResponseUserWeeklyScheduleExceptionDto,
  UpdateAgendaDto,
  WeeklyScheduleDto,
} from "../../api/models";
import { LoggedUserService } from "@keus-template/auth";
import { forkJoin, of, tap } from "rxjs";
import {
  UserWeeklyScheduleApiService,
  UserWeeklyScheduleExceptionsApiService,
} from "../api";
import { AgendaMapperService } from "../mappers";
import { environment } from "../../environments/environment";
import { CachedServiceBase } from "../base/cached-service.base";

/**
 * Service for managing agenda state including weekly schedules and exceptions
 * Handles state management for both doctors and clinics
 * Provides reactive state updates and change tracking
 */
@Injectable({
  providedIn: "root",
})
export class AgendaStateService extends CachedServiceBase<ResponseUserWeeklyScheduleExceptionDto> {
  /** Injected service for logged user data */
  private readonly _loggedUserService: LoggedUserService =
    inject(LoggedUserService);

  /** Injected service for weekly schedule API operations */
  private readonly _userWeeklyScheduleApiService: UserWeeklyScheduleApiService =
    inject(UserWeeklyScheduleApiService);

  /** Injected service for weekly schedule exceptions API operations */
  private readonly _userWeeklyScheduleExceptionsApiService: UserWeeklyScheduleExceptionsApiService =
    inject(UserWeeklyScheduleExceptionsApiService);

  /** Injected service for agenda mapping */
  private readonly _agendaMapperService: AgendaMapperService =
    inject(AgendaMapperService);

  /** The private weekly schedule data of the user */
  private readonly _weeklySchedule: WritableSignal<ResponseUserWeeklyScheduleDto | null> =
    signal(null);

  /** The public weekly schedule data of the user */
  public readonly weeklySchedule: Signal<ResponseUserWeeklyScheduleDto | null> =
    this._weeklySchedule.asReadonly();

  /** Flag to track if initial data has been loaded from backend */
  private readonly _isInitialDataLoaded: WritableSignal<boolean> = signal(false);

  /** Public signal to check if initial data has been loaded */
  public readonly isInitialDataLoaded: Signal<boolean> = this._isInitialDataLoaded.asReadonly();

  /** The private weekly schedule exceptions data of the user */
  private readonly _exceptions: WritableSignal<
    ResponseUserWeeklyScheduleExceptionDto[] | null
  > = signal(null);

  /** The public weekly schedule exceptions data of the user */
  public readonly exceptions: Signal<
    ResponseUserWeeklyScheduleExceptionDto[] | null
  > = this._exceptions.asReadonly();

  /** The snapshot of the initial state for change tracking */
  private readonly _initialState: WritableSignal<{
    schedule: ResponseUserWeeklyScheduleDto | null;
    exceptions: ResponseUserWeeklyScheduleExceptionDto[] | null;
  }> = signal({
    schedule: null,
    exceptions: null,
  });

  /** Signal that indicates if there are unsaved changes by comparing current and initial state */
  public readonly hasUnsavedChanges: Signal<boolean> = computed(() => {
    const initialState = this._initialState();
    const currentState = {
      schedule: this._weeklySchedule(),
      exceptions: this._exceptions(),
    };

    const updateDto = this._agendaMapperService.mapUpdateAgendaDto(
      initialState,
      currentState,
    );

    return Object.keys(updateDto).length > 0;
  });

  /** Signal tracking if the schedule form is valid */
  private readonly _isScheduleFormValid: WritableSignal<boolean> = signal(true);

  /** Public signal to check if schedule form is valid */
  public readonly isScheduleFormValid: Signal<boolean> = this._isScheduleFormValid.asReadonly();

  /**
   * Updates the schedule form validity state
   * @param isValid - Whether the form is valid
   */
  public setScheduleFormValidity(isValid: boolean): void {
    this._isScheduleFormValid.set(isValid);
  }

  /** Signal that indicates if changes can be saved (has changes AND form is valid) */
  public readonly canSaveChanges: Signal<boolean> = computed(() => {
    return this.hasUnsavedChanges() && this.isScheduleFormValid();
  });

  /**
   * Initializes the agenda state by loading user data
   */
  public initState(): void {
    this._loadInitialUserData();
  }

  /**
   * Returns the exceptions data as it is, no need for additional filtering.
   * @returns the current exception schedule data.
   */
  protected override getFilteredData(): Signal<ResponseUserWeeklyScheduleExceptionDto[]> {
    return computed(() => {
      const exceptions = this._exceptions();
      return exceptions ? exceptions.sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }) : [];
    });
  }

  /**
   * Updates the weekly schedule with new data.
   */
   public refresh(): void {
    this._loadInitialUserData();
  }

  public reset(): void {
    this.clearData();
  }

  /**
   * Updates the weekly schedule with new data
   * @param scheduleDto The new weekly schedule data
   */
  public setWeeklySchedule(scheduleDto: WeeklyScheduleDto): void {
    const value = this._weeklySchedule();
    if (value) {
      this._weeklySchedule.set({ ...value, schedule: scheduleDto });
    } else if (this._hasScheduleData(scheduleDto)) {
      // Only create a new schedule if there's actual data
      this._weeklySchedule.set({
        schedule: scheduleDto,
        email: this._loggedUserService.currentUser()?.email || "",
        tenant: environment.tenant,
      } as ResponseUserWeeklyScheduleDto);
    }
  }

  /**
   * Checks if the schedule DTO contains any actual time ranges
   * @param scheduleDto The schedule to check
   * @returns true if the schedule has at least one time range
   */
  private _hasScheduleData(scheduleDto: WeeklyScheduleDto): boolean {
    return Object.values(scheduleDto).some(
      (daySchedule) => daySchedule && daySchedule.length > 0,
    );
  }

  /**
   * Loads initial user data including schedule and exceptions
   * Handles errors gracefully and processes the data when loaded
   */
  private _loadInitialUserData(): void {
    forkJoin({
      schedule: this._userWeeklyScheduleApiService.findOne$(),
      exceptions:
        this._userWeeklyScheduleExceptionsApiService.findAllByEmail$(),
    })
      .pipe(
        tap(({ schedule, exceptions }) => {
          this._processUserSetupData(schedule, exceptions);
        }),
      )
      .subscribe();
  }

  /**
   * Processes the loaded user data and sets up initial state
   * @param schedule The user's weekly schedule
   * @param exceptions The user's schedule exceptions
   */
  private _processUserSetupData(
    schedule: ResponseUserWeeklyScheduleDto | null,
    exceptions: ResponseUserWeeklyScheduleExceptionDto[] | null,
  ): void {
    this._weeklySchedule.set(schedule);
    this._exceptions.set(exceptions);
    // Initialize base class data for pagination
    this._data.set(exceptions ?? []);

    this._initialState.set(
      Object.freeze({
        schedule: structuredClone(schedule),
        exceptions: structuredClone(exceptions),
      }),
    );

    // Mark as loaded after processing initial data
    this._isInitialDataLoaded.set(true);
  }

  /**
   * Creates or updates an exception for a specific date
   * @param exception The exception data to insert or update
   */
  public upsertExceptionByDate(
    exception: CreateUserWeeklyScheduleExceptionDto,
  ): void {
    const current = this._exceptions() ?? [];
    const index = current.findIndex((e) => e.date === exception.date);
    
    let updated = [...current];
    if (index >= 0) {
      updated[index] = { ...current[index], ...exception };
    } else {
      updated.push(exception as ResponseUserWeeklyScheduleExceptionDto);
    }
    
    // Sort by date and update both signals
    updated.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    this._updateExceptions(updated);
  }

  /**
   * Removes an exception for a specific date
   * @param exceptionDate The date of the exception to remove
   */
  public removeException(exceptionDate: string): void {
    const current = this._exceptions() ?? [];
    const filtered = current.filter((e) => e.date !== exceptionDate);
    this._updateExceptions(filtered);
  }

  /**
   * Updates both exception signals to maintain consistency
   * @param exceptions The updated exceptions array
   * @private
   */
  private _updateExceptions(exceptions: ResponseUserWeeklyScheduleExceptionDto[]): void {
    this._exceptions.set(exceptions);
    this._data.set(exceptions); // Keep base class pagination in sync
  }

  /**
   * Generates the update DTO containing only changed data
   * @returns UpdateAgendaDto with changes to be saved
   */
  public getUpdateAgendaDto(): UpdateAgendaDto {
    const initialState = this._initialState();
    const currentState = {
      schedule: this._weeklySchedule(),
      exceptions: this._exceptions(),
    };
    this._data.set(this.exceptions() ?? []);

    return this._agendaMapperService.mapUpdateAgendaDto(
      initialState,
      currentState,
    );
  }

  /**
   * Updates the initial state snapshot after successful save
   * Used to reset the change tracking baseline
   */
  public updateInitialState(): void {
    this._initialState.set(
      Object.freeze({
        schedule: structuredClone(this._weeklySchedule()),
        exceptions: structuredClone(this._exceptions()),
      }),
    );
    this._data.set(this.exceptions() ?? []);
  }

  /**
   * Legacy compatibility - should show pagination
   */
  public shouldShowPagination(): boolean {
    return this.paginationInfo().shouldShowPagination;
  }

   /**
   * Legacy compatibility - using base class pagination
   */
  public nextPage(): void {
    this.goToNextPage();
  }

  /**
   * Legacy compatibility - using base class pagination
   */
  public previousPage(): void {
    this.goToPreviousPage();
  }

  /**
   * Legacy compatibility - get current page index
   */
  public currentPageIndex(): number {
    return this.paginationInfo().currentPage;
  }

  /**
   * Legacy compatibility - has next page
   */
  public hasNextPage(): boolean {
    return this.paginationInfo().hasNextPage;
  }

  /**
   * Legacy compatibility - has previous page
   */
  public hasPreviousPage(): boolean {
    return this.paginationInfo().hasPreviousPage;
  }

  /**
   * Legacy compatibility - get total pages
   */
  public totalPages(): number {
    return this.paginationInfo().totalPages;
  }
}
